import {
  useEnableParameterOverrides,
  useShowVpnTools,
} from "../business-components/adminSettings/AdminContext";
import { isAppTypeIframe } from "../getAppType";
import { CvpSettings } from "../settings/CvpSettings";
import { useSettings } from "../settings/useSettings";
import { getRedirectProps, IGetRedirectPropsResponse } from "./redirect";
import { useCurrentPath } from "./useCurrentPath";
import { useSearchParam } from "./useSearchParam";
import { CVPSearchParams } from "./wsQueryStringStore";

export interface GetLoginPropsArgs {
  /** Default is currentPath */
  cvpalru?: string;

  showVpnTools: boolean;
  isVpnLoginButton?: boolean;
  authPath?: string;
  cvppwa: boolean;
  enableParameterOverrides: boolean;
  cvpcid?: string;
  cvpce?: string;
}

export function useLoginProps(
  args: Omit<
    GetLoginPropsArgs,
    "showVpnTools" | "authPath" | "cvppwa" | "enableParameterOverrides"
  > = {}
): LoginProps {
  const settings = useSettings();
  const currentPath = useCurrentPath();
  const showVpnTools = useShowVpnTools();
  const enableParameterOverrides = useEnableParameterOverrides();

  const cvppwa = !!useSearchParam(CVPSearchParams.cvppwa);

  return getLoginProps(currentPath, settings, {
    ...args,
    enableParameterOverrides,
    showVpnTools,
    cvppwa,
  });
}

export function getLoginProps(
  currentPath: string,
  settings: CvpSettings,
  args: GetLoginPropsArgs
): LoginProps {
  const {
    enableParameterOverrides,
    authPath = "/auth/keycloak/login",
    cvppwa,
  } = args;

  // extra step for choosing auth or skipping it
  if (args.showVpnTools && !args.isVpnLoginButton) {
    return {
      redirectProps: getRedirectProps("vpn-login", {
        mergeQs: {
          [CVPSearchParams.cvpalru]: args.cvpalru,
        },
      }),
    };
  }

  let { cvpalru } = getUrlsToGoToAfterLogin(currentPath, args, cvppwa);

  if (isAppTypeIframe() && settings.iframe_loginOldPortal) {
    return {
      redirectProps: {
        href: `https://secure.webtemp.no/WebtempCVPortal/default.aspx?k=${window.cvpIdConcern}`,
        target: "_blank",
      },
    };
  }

  const searchParams = new URLSearchParams();
  searchParams.set("cvpalru", cvpalru);

  if (window.cvpIdConcern) {
    searchParams.set("k", window.cvpIdConcern.toString());
  }
  if (enableParameterOverrides) {
    searchParams.set("enableParameterOverride", "true");
  }

  if (args.cvpcid) searchParams.set("cvpcid", args.cvpcid);
  if (args.cvpce) searchParams.set("cvpce", args.cvpce);

  const qs = searchParams.toString();

  return {
    redirectProps: getRedirectProps(`${location.origin}${authPath}?${qs}`, {
      mergeQs: {},
      redirectRaw: true,
    }),
  };
}

function getUrlsToGoToAfterLogin(
  currentPath: string,
  args: GetLoginPropsArgs,
  cvppwa: boolean
) {
  let { cvpalru = currentPath } = args;

  cvpalru = urlCorrections(cvpalru);

  const _cvpalru = getRedirectProps(cvpalru, {
    mergeQs: {
      k: window.cvpIdConcern ?? null,
      [CVPSearchParams.cvppwa]: cvppwa ? "1" : null,
    },
  }).href!;

  return {
    cvpalru: _cvpalru,
  };
}

function urlCorrections(url: string): string {
  // add leading slash if missing
  if (!url.startsWith("/")) {
    url = `/${url}`;
  }

  if (url === "/login") {
    // dont wanna go to this page after login
    url = "/";
  }

  if (url === "/vpn-login") {
    url = "/";
  }

  if (url === "/applicationsent") {
    // dont wanna go to this page after login
    url = "/profile";
  }

  return url;
}

export interface LoginProps {
  redirectProps: IGetRedirectPropsResponse;
}
